import { motion, AnimatePresence } from "framer-motion";
import { cloneDeep } from "lodash";
import {
  ButtonGhostV2,
  colors,
  SANS_3,
  Accordion,
  colors2,
  transition,
  decideLinkCompAndTarget,
} from "oolib";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { genIcon } from "oolib/dist/v2/components/Dropdowns/utils/genIcon";
import { SidebarLayoutV2 } from "@/components/layout/SidebarLayoutV2";
import { ButtonPrimarySidebarNav } from "./ButtonPrimarySidebarNav";
import { __CheckAccessViaConfig } from "@/utils/accessControl";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { HeaderLogo } from "../Header/PrimaryHeaderV2/HeaderUI/comps/HeaderLogo";
import { useLogout } from "@/utils/react-query-hooks/auth";
import { useLocalStorage } from "@/utils/customHooks/useLocalStorage";
import { getAnnoMode } from "@/utils/getAnnoMode";

const NormalizedButton = styled.button`
  border: none;
  background-color: ${colors.none};
`;

const StyledSidebarContentsHeader = styled.div`
  height: ${(props) => props.headerHeight};
  display: flex;
  align-items: center;
  padding: 0 2rem;
  border-bottom: 1px solid ${colors.greyColor10};
  position: relative;
`;

const StyledHamburgerWrapper = styled(motion.div)`
  position: absolute;
`;

const StyledMenuAndXFlex = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 auto;
`;

const StyledSectionWrapper = styled.div`
  ${transition("padding")};
  padding: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ top_bottom }) =>
    top_bottom === "top"
      ? css`
          &:not(:last-child) {
            border-bottom: 1px solid ${colors2.grey10};
          }
        `
      : css`
          &:not(:first-child) {
            border-top: 1px solid ${colors2.grey10};
          }
        `}
`;

const checkMenuItem = (mi, user, deployment, annoMode) => {
  if (mi.addOnModule) {
    if (!deployment?.selfManaged_addOnModules?.[mi.addOnModule]?.enable) return undefined;
  }
  // An entry that only makes sense under one annotation mode (e.g. the
  // Priorities page reads the split-mode topic tree).
  if (mi.annoMode && mi.annoMode !== annoMode) return undefined;
  if (mi.accessConfig) {
    if (!__CheckAccessViaConfig({ user, ...mi.accessConfig })) return undefined;
  }
  return mi;
};

const filterMenuItemsByAccess = (menuItems = [], user, deployment, annoMode) => {
  return menuItems
    .map((item) => {
      // Case 1: Sectioned menu item (has sectionId)
      if (item.sectionId && item.menuItems) {
        const filteredSection = item.menuItems
          .map((mi) => checkMenuItem(mi, user, deployment, annoMode))
          .filter(Boolean);
        return filteredSection.length
          ? { ...item, menuItems: filteredSection }
          : undefined;
      }

      // Case 2: Single menu item
      return checkMenuItem(item, user, deployment, annoMode);
    })
    .filter(Boolean);
};

const PrimarySidebarNav = ({
  config,
  headerHeight,
  gridHeightCSS = "100vh",
  lsbId = "PlatformBuilderShell",
  lsbWidth = 200,
  lsbClosedWidth = 46,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();
  // Persist the sidebar expand/collapse state so it loads in the same state next visit
  const [toggleState, setToggleState] = useLocalStorage(
    "sm-primary-sidebar-nav-toggle-state",
    { lsb: true, rsb: false }
  );
  const [accordionClosed, setAccordionClosed] = useState([]);
  const [preDrilldownPath, setPreDrilldownPath] = useState(null);
  const userData = useGetQueryData("userData");
  const platformConfigs = useGetQueryData("platformConfigs");
  const { _Nav, deployment } = platformConfigs;
  const annoMode = getAnnoMode(platformConfigs);

  // ✅ Filter top menu items with access config
  const topMenuItems = useMemo(() => {
    return filterMenuItemsByAccess(config.topMenuItems, userData?.user, deployment, annoMode);
  }, [config.topMenuItems, userData?.user, deployment, annoMode]);

  // ✅ Filter and extend bottom menu items
  const bottomMenuItems = useMemo(() => {
    const filtered = filterMenuItemsByAccess(
      config.bottomMenuItems,
      userData?.user,
      deployment,
      annoMode
    );

    return [
      ...(filtered || []),
      {
        sectionId: "b_fixed_1",
        menuItems: [
          {
            value: "collapseSidebar",
            icon: toggleState.lsb ? "ArrowLineLeft" : "ArrowLineRight",
            display: "Collapse Side Panel",
            onClick: () =>
              setToggleState((prev) => ({
                ...prev,
                lsb: !prev.lsb,
              })),
          },
        ],
      },
    ];
  }, [config.bottomMenuItems, userData?.user, deployment, annoMode, toggleState.lsb]);

  // Derive active drilldown from current URL
  const activeDrilldown = useMemo(() => {
    const allMenuItems = [
      ...(topMenuItems || []),
      ...(bottomMenuItems || []),
    ];
    for (const item of allMenuItems) {
      const menuItems = item.menuItems || [item];
      for (const mi of menuItems) {
        if (mi.drilldownMenuItems) {
          const filtered = mi.drilldownMenuItems.filter(
            (d) => checkMenuItem(d, userData?.user, deployment, annoMode)
          );
          if (filtered.some((d) => d.to && location.pathname.startsWith(d.to))) {
            return filtered;
          }
        }
      }
    }
    return null;
  }, [topMenuItems, bottomMenuItems, location.pathname, userData?.user, deployment, annoMode]);

  // Helper function to get all accordion items from menu structure
  const getAllAccordionItems = (menuItems) => {
    const accordionItems = [];

    menuItems.forEach((item) => {
      if (item.sectionId && item.menuItems) {
        // Handle sectioned menu items
        item.menuItems.forEach((menuItem) => {
          if (menuItem.subMenu) {
            accordionItems.push(menuItem.value);
          }
        });
      } else if (item.subMenu) {
        // Handle direct accordion items
        accordionItems.push(item.value);
      }
    });

    return accordionItems;
  };

  const onClickFnsLookup = {
    logout: logout,
  };

  useEffect(() => {
    if (toggleState.lsb === false) {
      // When the sidebar shuts, shut all the accordions within it
      const allAccordionItems = getAllAccordionItems(topMenuItems || []);
      setAccordionClosed(allAccordionItems);
    } else {
      // If sidebar is opened, open all the accordions within it
      if (accordionClosed.length > 0) {
        setAccordionClosed([]);
      }
    }
  }, [toggleState, topMenuItems]);

  const renderMenuItem = (op) => {
    // Handle accordion items (with subMenu)
    if (op.subMenu) {
      return (
        <div key={op.value}>
          <ButtonPrimarySidebarNav
            icon={op.icon}
            display={op.display}
            value={op.value}
            sidebarOpen={toggleState.lsb}
            onClick={() =>
              setAccordionClosed((prev) => {
                const newAccClosed = cloneDeep(prev);
                if (newAccClosed.includes(op.value)) {
                  //if we are opening this accordion & the sidebar is shut, then open it
                  if (toggleState.lsb === false)
                    setToggleState((prev) => ({ ...prev, lsb: true }));
                  return newAccClosed.filter((p) => p !== op.value);
                } else {
                  newAccClosed.push(op.value);
                  return newAccClosed;
                }
              })
            }
            isAccordion
            accordionClosed={accordionClosed}
          />
          <Accordion
            expand={accordionClosed.includes(op.value) ? false : true}
            noHeader
          >
            {op.subMenu.map((subOp) => {
              return (
                <ButtonPrimarySidebarNav
                  key={subOp.value}
                  style={{ paddingLeft: "4.5rem" }}
                  active={location.pathname.includes(subOp.to)}
                  display={subOp.display}
                  value={subOp.value}
                  sidebarOpen={toggleState.lsb}
                  data-path={subOp.to}
                  onClick={subOp.onClick || (() => navigate(subOp.to))}
                />
              );
            })}
          </Accordion>
        </div>
      );
    } else {
      // Handle regular menu items and action items
      return (
        <ButtonPrimarySidebarNav
          key={op.value || op.display}
          icon={op.icon || "Cube"}
          active={op.to ? location.pathname.includes(op.to) : false}
          display={op.display}
          value={op.value}
          sidebarOpen={toggleState.lsb}
          data-path={op.to}
          data-action={op.onClickFn ? 'action' : undefined} // action buttons (logout)
          onClick={
            op.onClick ||
            (() => {
              if (op.drilldownMenuItems) {
                const filtered = op.drilldownMenuItems.filter(
                  (mi) => checkMenuItem(mi, userData?.user, deployment, annoMode)
                );
                setPreDrilldownPath(location.pathname);
                const firstRoute = filtered.find((mi) => mi.to);
                if (firstRoute) navigate(firstRoute.to);
                return;
              }
              if (op.onClickFn) {
                onClickFnsLookup[op.onClickFn]();
                return;
              }
              const { externalUrl } = decideLinkCompAndTarget({
                to: op.to,
                openInNewTab: true,
              });
              externalUrl ? window.open(op.to, "_blank") : navigate(op.to);
            })
          }
        />
      );
    }
  };

  // Reusable function to render menu items
  const renderMenuItems = ({ menuItems, top_bottom }) => {
    return menuItems.map((op, opIdx) => {
      if (op.sectionId) {
        return (
          <StyledSectionWrapper
            toggleState={toggleState}
            top_bottom={top_bottom}
            key={op.sectionId}
          >
            {op.menuItems.map(renderMenuItem)}
          </StyledSectionWrapper>
        );
      } else {
        return (
          <Fragment key={op.value || op.display}>{renderMenuItem(op)}</Fragment>
        );
      }
    });
  };

  const logoSection = (
    <div
      style={{
        padding: "6px 10px",
        borderBottom: `1px solid ${colors2.grey10}`,
      }}
    >
      <div
        style={{
          position: "relative",
          height: "35px",
        }}
      >
        <AnimatePresence>
          {toggleState.lsb ? (
            <motion.div
              key="full-logo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ position: "absolute", top: 0, left: 0 }}
            >
              <HeaderLogo config={{ _Nav, deployment }} />
            </motion.div>
          ) : (
            <motion.div
              key="short-logo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ position: "absolute", top: 0, left: 0 }}
            >
              <HeaderLogo config={{ _Nav, deployment }} variant="short" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  const sidebarContent = (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        background: colors2.grey2,
      }}
    >
      {activeDrilldown ? (
        <Fragment>
          <div>
            <div
              style={{
                padding: "6px 10px",
                borderBottom: `1px solid ${colors2.grey10}`,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <ButtonGhostV2
                icon="ArrowLeft"
                onClick={() => {
                  if (preDrilldownPath) {
                    navigate(preDrilldownPath);
                    setPreDrilldownPath(null);
                  } else {
                    navigate("/");
                  }
                }}
              />
              <div style={{ position: "relative", height: "35px", flex: 1 }}>
                <AnimatePresence>
                  {toggleState.lsb ? (
                    <motion.div
                      key="full-logo"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      style={{ position: "absolute", top: 0, left: 0 }}
                    >
                      <HeaderLogo config={{ _Nav, deployment }} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="short-logo"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      style={{ position: "absolute", top: 0, left: 0 }}
                    >
                      <HeaderLogo config={{ _Nav, deployment }} variant="short" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <StyledSectionWrapper top_bottom="top">
              {activeDrilldown.map(renderMenuItem)}
            </StyledSectionWrapper>
          </div>
          <div>
            {renderMenuItems({
              top_bottom: "bottom",
              menuItems: bottomMenuItems.filter((section) =>
                ["bottom_2", "b_fixed_1"].includes(section.sectionId)
              ),
            })}
          </div>
        </Fragment>
      ) : (
        <Fragment>
          <div>
            {logoSection}
            {renderMenuItems({
              top_bottom: "top",
              menuItems: topMenuItems || [],
            })}
          </div>
          <div>
            {renderMenuItems({
              top_bottom: "bottom",
              menuItems: bottomMenuItems,
            })}
          </div>
        </Fragment>
      )}
    </div>
  );

  return (
    <SidebarLayoutV2
      gridHeightCSS={gridHeightCSS}
      lsbId={lsbId}
      lsbWidth={lsbWidth}
      lsbClosedWidth={lsbClosedWidth}
      toggleState={toggleState}
      setToggleState={setToggleState}
      toggleAnimationStyle={"mask"}
      lsbComp={sidebarContent}
      children={children}
    />
  );
};

export default PrimarySidebarNav;
